import React, { useState, useEffect } from 'react';
import { Shield, Clock, CheckCircle, XCircle, AlertCircle, Trash2, Filter, Download } from 'lucide-react';

interface Log {
    id: string;
    timestamp: string;
    username: string;
    ip: string;
    userAgent: string;
    status: 'success' | 'failure' | 'pending';
}

interface LogStats {
    total: number;
    success: number;
    failure: number;
    pending: number;
}

export const SecurityLogsView: React.FC = () => {
    const [logs, setLogs] = useState<Log[]>([]);
    const [stats, setStats] = useState<LogStats>({ total: 0, success: 0, failure: 0, pending: 0 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<string>('all');
    const [searchUsername, setSearchUsername] = useState('');

    useEffect(() => {
        fetchLogs();
    }, [filter, searchUsername]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filter !== 'all') params.append('status', filter);
            if (searchUsername) params.append('username', searchUsername);

            const response = await fetch(`${import.meta.env.VITE_API_URL}/api/logs?${params}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) throw new Error('Failed to fetch logs');

            const data = await response.json();
            setLogs(data.logs);
            setStats(data.stats);
        } catch (error) {
            console.error('Error fetching logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const deleteLog = async (id: string) => {
        if (!confirm('Supprimer ce log ?')) return;

        try {
            await fetch(`${import.meta.env.VITE_API_URL}/api/logs/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            fetchLogs();
        } catch (error) {
            console.error('Error deleting log:', error);
        }
    };

    const exportLogs = () => {
        const csv = [
            ['Date', 'Utilisateur', 'IP', 'User Agent', 'Statut'].join(','),
            ...logs.map(log => [
                new Date(log.timestamp).toLocaleString('fr-FR'),
                log.username,
                log.ip,
                `"${log.userAgent}"`,
                log.status
            ].join(','))
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const dateStr = new Date().toISOString();
        const safeDate = dateStr ? dateStr.split('T')[0] : 'unknown-date';
        a.download = `security-logs-${safeDate}.csv`;
        a.click();
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'success': return <CheckCircle className="w-5 h-5 text-green-400" />;
            case 'failure': return <XCircle className="w-5 h-5 text-red-400" />;
            default: return <AlertCircle className="w-5 h-5 text-yellow-400" />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'success': return 'bg-green-500/20 text-green-400 border-green-500/50';
            case 'failure': return 'bg-red-500/20 text-red-400 border-red-500/50';
            default: return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Shield className="w-8 h-8 text-red-400" />
                    <div>
                        <h2 className="text-2xl font-bold text-gray-100">Logs de Sécurité</h2>
                        <p className="text-sm text-gray-400">Surveillance des tentatives de connexion</p>
                    </div>
                </div>

                <button
                    onClick={exportLogs}
                    className="glass-button flex items-center gap-2 text-gray-400 hover:text-primary"
                >
                    <Download className="w-4 h-4" />
                    Exporter CSV
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="glass-panel p-4 border border-white/10">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-400">Total</p>
                            <p className="text-2xl font-bold text-gray-100">{stats.total}</p>
                        </div>
                        <Clock className="w-8 h-8 text-blue-400 opacity-50" />
                    </div>
                </div>

                <div className="glass-panel p-4 border border-green-500/30 bg-green-500/5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-green-400">Réussites</p>
                            <p className="text-2xl font-bold text-green-400">{stats.success}</p>
                        </div>
                        <CheckCircle className="w-8 h-8 text-green-400 opacity-50" />
                    </div>
                </div>

                <div className="glass-panel p-4 border border-red-500/30 bg-red-500/5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-red-400">Échecs</p>
                            <p className="text-2xl font-bold text-red-400">{stats.failure}</p>
                        </div>
                        <XCircle className="w-8 h-8 text-red-400 opacity-50" />
                    </div>
                </div>

                <div className="glass-panel p-4 border border-yellow-500/30 bg-yellow-500/5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-yellow-400">Taux d'échec</p>
                            <p className="text-2xl font-bold text-yellow-400">
                                {stats.total > 0 ? ((stats.failure / stats.total) * 100).toFixed(1) : 0}%
                            </p>
                        </div>
                        <AlertCircle className="w-8 h-8 text-yellow-400 opacity-50" />
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="glass-panel p-4 flex flex-col md:flex-row gap-4">
                <div className="flex items-center gap-2 flex-1">
                    <Filter className="w-5 h-5 text-gray-400" />
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="glass-input flex-1"
                    >
                        <option value="all">Tous les statuts</option>
                        <option value="success">Réussites</option>
                        <option value="failure">Échecs</option>
                        <option value="pending">En attente</option>
                    </select>
                </div>

                <input
                    type="text"
                    placeholder="Rechercher un utilisateur..."
                    value={searchUsername}
                    onChange={(e) => setSearchUsername(e.target.value)}
                    className="glass-input flex-1"
                />
            </div>

            {/* Logs Table */}
            <div className="glass-panel overflow-hidden">
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto custom-scrollbar">
                    <table className="w-full">
                        <thead className="sticky top-0 bg-[#1a1b26] z-10">
                            <tr className="border-b border-white/10">
                                <th className="text-left p-4 text-sm font-semibold text-gray-300">Date & Heure</th>
                                <th className="text-left p-4 text-sm font-semibold text-gray-300">Utilisateur</th>
                                <th className="text-left p-4 text-sm font-semibold text-gray-300">Adresse IP</th>
                                <th className="text-left p-4 text-sm font-semibold text-gray-300">Navigateur/Appareil</th>
                                <th className="text-left p-4 text-sm font-semibold text-gray-300">Statut</th>
                                <th className="text-right p-4 text-sm font-semibold text-gray-300">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="p-8 text-center text-gray-400">
                                        Chargement...
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="p-8 text-center text-gray-400">
                                        Aucun log trouvé
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log) => (
                                    <tr key={log.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <Clock className="w-4 h-4 text-gray-500" />
                                                <span className="text-sm text-gray-300">
                                                    {new Date(log.timestamp).toLocaleString('fr-FR')}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <span className="text-sm font-medium text-gray-200">{log.username}</span>
                                        </td>
                                        <td className="p-4">
                                            <code className="text-xs bg-white/5 px-2 py-1 rounded text-blue-300">
                                                {log.ip}
                                            </code>
                                        </td>
                                        <td className="p-4">
                                            <span className="text-xs text-gray-400 max-w-xs truncate block">
                                                {log.userAgent}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                {getStatusIcon(log.status)}
                                                <span className={`text-xs px-2 py-1 rounded border ${getStatusColor(log.status)}`}>
                                                    {log.status === 'success' ? 'Succès' : log.status === 'failure' ? 'Échec' : 'En attente'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="p-4 text-right">
                                            <button
                                                onClick={() => deleteLog(log.id)}
                                                className="text-red-400 hover:text-red-300 transition-colors"
                                                title="Supprimer"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Footer Info */}
            <div className="glass-panel p-4 border border-yellow-500/30 bg-yellow-500/5">
                <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-200/80">
                        <p className="font-semibold mb-1">Politique de rétention des données (RGPD)</p>
                        <p className="text-xs text-yellow-200/60">
                            Les logs sont automatiquement supprimés après 90 jours.
                            Les adresses IP sont des données personnelles et sont stockées uniquement à des fins de sécurité.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
